import { findNodeByCode } from "@/helpers/datasetAttributeHelper";
import { buildFilterFromResponse } from "@/helpers/filterHelper";
import type { AdminAreaHierarchy } from "@/types/adminArea";
import {
  DataSchemaType,
  type DatasetDetailSchemaResponse,
  type DatasetFilter,
  type DatasetFilterPayload,
  type DateFilter,
  EFilterOptionType,
  type MinMaxFilter,
  type MultipleSelectFilter
} from "@/types/dataset";
import type {
  DatasetConfigAttributeResponse,
  GridConfigResponse
} from "@/types/project";

export const getAggregationConfig = (
  gridConfig: GridConfigResponse | null | undefined
) => {
  if (gridConfig) {
    return {
      aggregationType: gridConfig?.aggregation_type,
      resolution: gridConfig?.level
    };
  }
  return null;
};

// Compares attribute_code values, treating empty string, undefined, and null as equivalent.
const isAttributeCodeMatch = (
  schemaCode: string | undefined | null,
  attrCode: string | undefined
) => (schemaCode || "") === (attrCode || "");

// Resolves the schema item and the canonical attribute id for a given attribute.
// When saving, paired_key_attribute_id is used as the attributeId. When opening,
// we find the schema item that owns that paired_key and use its actual id instead.
const resolveSchemaItem = (
  attrId: string,
  attrCode: string | undefined,
  schema: DatasetDetailSchemaResponse[]
) => {
  const schemaItemByPairedKey = schema.find(
    (item) =>
      item.paired_key_attribute_id === attrId &&
      isAttributeCodeMatch(item.attribute_code, attrCode)
  );

  const schemaItem =
    schemaItemByPairedKey ??
    schema.find(
      (item) =>
        item.id === attrId &&
        isAttributeCodeMatch(item.attribute_code, attrCode)
    );

  const resolvedAttributeId = schemaItemByPairedKey
    ? schemaItemByPairedKey.id
    : attrId;

  return { schemaItem, resolvedAttributeId };
};

export const buildDatasetFilter = (
  attributes: DatasetConfigAttributeResponse[],
  schema: DatasetDetailSchemaResponse[]
) => {
  const getPosition = (filterId: string, attributeCode = "") =>
    schema.find(
      (item) =>
        item.id === filterId &&
        isAttributeCodeMatch(item.attribute_code, attributeCode)
    )?.position;

  return attributes
    .filter((attr) => attr.filter?.criteria)
    .map((attr) => {
      const { schemaItem, resolvedAttributeId } = resolveSchemaItem(
        attr.id,
        attr.attribute_code,
        schema
      );

      // attr.filter is guaranteed non-null by the .filter() predicate above
      const filter = attr.filter as NonNullable<typeof attr.filter>;

      return {
        ...buildFilterFromResponse(filter),
        attributeCode: attr.attribute_code ?? "",
        filterId: `${resolvedAttributeId}_${getPosition(resolvedAttributeId, attr.attribute_code)}`,
        filterLabel: schemaItem?.attribute_name ?? "",
        attributeId: resolvedAttributeId,
        paired_key_attribute_id: schemaItem?.paired_key_attribute_id ?? null,
        is_multiple_adm_area: filter.is_multiple_adm_area ?? false
      };
    });
};

export const buildDatasetFilterFromPayload = (
  attributes: DatasetFilterPayload[],
  schema: DatasetDetailSchemaResponse[]
): DatasetFilter[] => {
  const inferFilterType = (
    filter: DatasetFilterPayload["filter"],
    dataType?: string
  ): EFilterOptionType => {
    if (!filter || Array.isArray(filter)) return EFilterOptionType.MULTIPLE;
    if ("min" in filter || "max" in filter) return EFilterOptionType.MIN_MAX;
    if (dataType === DataSchemaType.DATE_TIME)
      return EFilterOptionType.DATE_TIME;
    if (dataType === DataSchemaType.TIME) return EFilterOptionType.TIME;
    return EFilterOptionType.DATE;
  };

  return attributes
    .filter(
      (
        attr
      ): attr is DatasetFilterPayload & {
        filter: NonNullable<DatasetFilterPayload["filter"]>;
      } => attr.filter !== undefined
    )
    .map((attr) => {
      const { schemaItem, resolvedAttributeId } = resolveSchemaItem(
        attr.id,
        attr.attribute_code,
        schema
      );

      const filterType = inferFilterType(attr.filter, schemaItem?.data_type);
      const filter = attr.filter;

      const buildFilterFields = (): Partial<DatasetFilter> => {
        if (filterType === EFilterOptionType.MULTIPLE) {
          return { selectedOptions: filter as MultipleSelectFilter };
        }

        if (filterType === EFilterOptionType.MIN_MAX) {
          const f = filter as MinMaxFilter;
          return {
            minMaxRange: {
              min: f.min !== null ? String(f.min) : "",
              max: f.max !== null ? String(f.max) : "",
              includeNulls: f.include_nulls
            }
          };
        }

        if (filterType === EFilterOptionType.DATE_TIME) {
          const f = filter as DateFilter;
          const [fromDate = "", fromTime = ""] = (f.start ?? "").split("T");
          const [toDate = "", toTime = ""] = (f.end ?? "").split("T");
          return {
            dateTimeRange: {
              from: {
                date: fromDate !== "null" ? fromDate : "",
                time: fromTime.replace(":00Z", "").replace(":00.000Z", "")
              },
              to: {
                date: toDate !== "null" ? toDate : "",
                time: toTime.replace(":00Z", "").replace(":00.000Z", "")
              },
              includeNulls: f.include_nulls
            }
          };
        }

        if (filterType === EFilterOptionType.TIME) {
          const f = filter as DateFilter;
          const [, fromTime = ""] = (f.start ?? "").split("T");
          const [, toTime = ""] = (f.end ?? "").split("T");
          return {
            timeRange: {
              from: fromTime.replace(":00.000Z", "").replace(":00Z", ""),
              to: toTime.replace(":00.000Z", "").replace(":00Z", ""),
              includeNulls: f.include_nulls
            }
          };
        }

        // DATE
        const f = filter as DateFilter;
        const [fromDate = ""] = (f.start ?? "").split("T");
        const [toDate = ""] = (f.end ?? "").split("T");
        return {
          dateRange: {
            from: fromDate !== "null" ? fromDate : "",
            to: toDate !== "null" ? toDate : "",
            includeNulls: f.include_nulls
          }
        };
      };

      return {
        ...buildFilterFields(),
        filterId: `${resolvedAttributeId}_${schemaItem?.position}`,
        filterLabel: schemaItem?.attribute_name ?? "",
        filterType,
        attributeId: resolvedAttributeId,
        attributeCode: attr.attribute_code ?? "",
        paired_key_attribute_id: schemaItem?.paired_key_attribute_id ?? null,
        is_multiple_adm_area: false,
        time_format: null
      };
    });
};

/**
 * Enriches admin area filter options that contain only a bare `full_code`
 * (e.g. `"1304"`) into the canonical `"<UUID>::<full_code>"` format
 * (e.g. `"adad131313::1304"`) by looking up each code in the provided
 * admin area hierarchy tree.
 *
 * Options already in `"<UUID>::<full_code>"` format are left untouched.
 * If a bare code cannot be found in the hierarchy it is kept as-is.
 *
 * @param {DatasetFilter[]} filters - The filters to enrich.
 * @param {AdminAreaHierarchy[]} hierarchy - The hierarchy tree to search for UUIDs.
 * @returns {DatasetFilter[]} The enriched filters.
 */
export const enrichAdminAreaFilters = (
  filters: DatasetFilter[],
  hierarchy: AdminAreaHierarchy[]
): DatasetFilter[] =>
  filters.map((filter) => {
    if (
      !filter.attributeCode?.includes("adm_area_level") ||
      !filter.selectedOptions
    ) {
      return filter;
    }

    return {
      ...filter,
      selectedOptions: filter.selectedOptions.map((opt) => {
        if (opt.includes("::")) return opt;
        const node = findNodeByCode(opt, hierarchy);
        return node ? `${node.id}::${node.full_code}` : opt;
      })
    };
  });
